import { useEffect, useMemo, useRef, useState } from "react";
import t from "@/lang/lang";
import { useQpmBootstrapUpdateServiceProgramMutation } from "@/services/backend/qpm/bootstrap/api/bootstrap-api";
import { useQpmCatalogListQuery } from "@/services/backend/qpm/catalog/api/catalog-api";
import {
  useQpmOfferConfigGetSettingsQuery,
  useLazyQpmOfferConfigServiceProgramPreviewPDFQuery,
  useQpmOfferConfigPutSettingsMutation,
} from "@/services/backend/qpm/offer-config/api/offer-config-api";
import { PestKind } from "@/services/backend/qpm/pests/types/pest-enum";
import { useQpmPestListQuery } from "@/services/backend/qpm/pests/api/pest-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  Card,
  CardContent,
  CardDescription,
  CardTitle,
} from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Skeleton } from "@/shared/components/ui/skeleton";
import {
  ServiceProgramEntryEditor,
  ServiceProgramEntryValue,
  ServiceProgramGroupValue,
} from "@/shared/components/domain/qpm/service-program-entry-editor";
import { useDebouncedDirtyMutationWithPersistenceStateContextUpdate } from "@/shared/lib/debounce/debounce";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { DocumentSheetPreview } from "@/shared/bsm/core/document/components/ui/document-sheet";
import { useActiveCompany } from "@/shared/lib/active-company/active-company-provider";

export function ServiceProgramBaseDataRoutePage() {
  const { companyId } = useActiveCompany();
  const {
    data,
    error,
    isLoading,
    refetch: refetchSettings,
  } = useQpmOfferConfigGetSettingsQuery({});
  const [putSettings, putSettingsState] =
    useQpmOfferConfigPutSettingsMutation();
  const [seedServiceProgram, seedServiceProgramState] =
    useQpmBootstrapUpdateServiceProgramMutation();
  const [loadServiceProgramPreview, serviceProgramPreviewState] =
    useLazyQpmOfferConfigServiceProgramPreviewPDFQuery();
  const { data: pestCategoriesData, isLoading: pestCategoriesLoading } =
    useQpmPestListQuery({
      company: { active: true, values: [companyId] },
      kind: PestKind.Category,
      limit: 1,
      offset: 0,
    });
  const { data: pestIndicatorsData, isLoading: pestIndicatorsLoading } =
    useQpmPestListQuery({
      company: { active: true, values: [companyId] },
      kind: PestKind.Indicator,
      limit: 1,
      offset: 0,
    });
  const { data: catalogData, isLoading: catalogLoading } =
    useQpmCatalogListQuery({
      company: { active: true, values: [companyId] },
      limit: 1,
      offset: 0,
    });

  const [entries, setEntries] = useState<ServiceProgramEntryValue[]>([]);
  const [groups, setGroups] = useState<ServiceProgramGroupValue[]>([]);
  const [isEditorHydrated, setIsEditorHydrated] = useState(false);
  const [isAutoSaveEnabled, setIsAutoSaveEnabled] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const topStickyHeaderRef = useRef<HTMLDivElement | null>(null);
  const [topStickyHeaderHeight, setTopStickyHeaderHeight] = useState(0);
  const appHeaderOffset = 64;
  const groupsStickyTop = appHeaderOffset + topStickyHeaderHeight;
  const isInitialized = Boolean(data?.id);
  const hasPestCategories = (pestCategoriesData?.items?.length ?? 0) > 0;
  const hasPestIndicators = (pestIndicatorsData?.items?.length ?? 0) > 0;
  const hasCatalog = (catalogData?.items?.length ?? 0) > 0;
  const isInitPrerequisiteLoading =
    pestCategoriesLoading || pestIndicatorsLoading || catalogLoading;
  const canInitializeServiceProgram =
    hasPestCategories && hasPestIndicators && hasCatalog;

  useEffect(() => {
    if (!isInitialized) {
      setEntries([]);
      setGroups([]);
      setIsEditorHydrated(false);
      setIsAutoSaveEnabled(false);
      return;
    }
    if (isEditorHydrated) {
      return;
    }
    setEntries(data?.serviceProgramEntries ?? []);
    setGroups(data?.serviceProgramGroups ?? []);
    setIsEditorHydrated(true);
    setIsAutoSaveEnabled(false);
  }, [
    data?.serviceProgramEntries,
    data?.serviceProgramGroups,
    isEditorHydrated,
    isInitialized,
  ]);

  useEffect(() => {
    const node = topStickyHeaderRef.current;
    if (!node) {
      return () => {};
    }

    const updateHeight = () => {
      setTopStickyHeaderHeight(Math.round(node.getBoundingClientRect().height));
    };

    updateHeight();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(updateHeight);
      observer.observe(node);
      window.addEventListener("resize", updateHeight);
      return () => {
        observer.disconnect();
        window.removeEventListener("resize", updateHeight);
      };
    }

    window.addEventListener("resize", updateHeight);
    return () => {
      window.removeEventListener("resize", updateHeight);
    };
  }, [isInitialized]);

  const request = useMemo(
    () => ({
      id: data?.id,
      ceoName: data?.ceoName ?? "",
      ceoGender: data?.ceoGender ?? "",
      allowedDiscounts: data?.allowedDiscounts ?? [],
      allowedSurcharges: data?.allowedSurcharges ?? [],
      offerClaimText: data?.offerClaimText ?? "",
      serviceProgramGroups: groups,
      serviceProgramEntries: entries,
    }),
    [
      data?.allowedDiscounts,
      data?.allowedSurcharges,
      data?.ceoGender,
      data?.ceoName,
      data?.id,
      data?.offerClaimText,
      groups,
      entries,
    ],
  );

  const baselineRequest = useMemo(
    () => ({
      id: data?.id,
      ceoName: data?.ceoName ?? "",
      ceoGender: data?.ceoGender ?? "",
      allowedDiscounts: data?.allowedDiscounts ?? [],
      allowedSurcharges: data?.allowedSurcharges ?? [],
      offerClaimText: data?.offerClaimText ?? "",
      serviceProgramGroups: data?.serviceProgramGroups ?? [],
      serviceProgramEntries: data?.serviceProgramEntries ?? [],
    }),
    [
      data?.allowedDiscounts,
      data?.allowedSurcharges,
      data?.ceoGender,
      data?.ceoName,
      data?.id,
      data?.offerClaimText,
      data?.serviceProgramEntries,
      data?.serviceProgramGroups,
    ],
  );

  const isRequestDirty = useMemo(
    () => JSON.stringify(request) !== JSON.stringify(baselineRequest),
    [baselineRequest, request],
  );

  useDebouncedDirtyMutationWithPersistenceStateContextUpdate(
    request,
    putSettings,
    putSettingsState.isLoading,
    putSettingsState.error,
    putSettingsState.isSuccess,
    putSettingsState.reset,
    650,
    {
      toastError: true,
      toastSuccess: false,
      skip:
        !isInitialized ||
        !isEditorHydrated ||
        !isAutoSaveEnabled ||
        putSettingsState.isLoading,
      baselineKey: data?.id ?? "new",
      isDirty: (currentRequest, requestBaseline) =>
        JSON.stringify(currentRequest) !== JSON.stringify(requestBaseline),
    },
  );

  const onOpenPreview = async () => {
    try {
      await loadServiceProgramPreview({}).unwrap();
      setIsPreviewOpen(true);
    } catch {
      setIsPreviewOpen(false);
    }
  };

  const onSaveNow = async () => {
    try {
      await putSettings(request).unwrap();
    } catch {
      // handled by RTKQueryErrorAlert
    }
  };

  const onSeedDefaults = async () => {
    try {
      await seedServiceProgram({}).unwrap();
      await refetchSettings();
    } catch {
      // handled by RTKQueryErrorAlert
    }
  };

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }
  if (isLoading) {
    return <Skeleton className="h-32 w-full" />;
  }

  return (
    <>
      <Card>
        <div
          ref={topStickyHeaderRef}
          className="bg-card sticky top-16 z-30 border-b px-6 py-3"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-0.5">
              <CardTitle>{t("Leistungsprogramm")}</CardTitle>
              <CardDescription className="leading-snug">
                {t(
                  "Pflegen Sie Textbausteine inkl. Bedingungen. Einträge ohne Bedingungen sind statisch und werden immer angezeigt.",
                )}
              </CardDescription>
            </div>
            <div className="flex flex-col items-end gap-1">
              {!data?.id ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      className="cursor-pointer"
                      onClick={onSeedDefaults}
                      disabled={
                        seedServiceProgramState.isLoading ||
                        isInitPrerequisiteLoading ||
                        !canInitializeServiceProgram
                      }
                    >
                      {t("Leistungsprogramm initialisieren")}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t("Leistungsprogramm initialisieren")}
                  </TooltipContent>
                </Tooltip>
              ) : null}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    className="cursor-pointer"
                    onClick={onOpenPreview}
                    disabled={
                      !isInitialized || serviceProgramPreviewState.isFetching
                    }
                  >
                    {t("PDF")}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t("PDF Vorschau öffnen")}</TooltipContent>
              </Tooltip>
              <PersistenceStateUsingContext className="h-5 justify-end text-xs" />
            </div>
          </div>
        </div>
        <CardContent className="space-y-4 pt-3">
          {isInitialized ? (
            <>
              {isEditorHydrated && isRequestDirty ? (
                <div className="text-muted-foreground rounded-md border px-3 py-2 text-xs">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span>
                      {t(
                        "Ungespeicherte Änderungen sind vorhanden und werden erst nach dem automatischen Speichern in der PDF Vorschau sichtbar.",
                      )}
                    </span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="default"
                          size="sm"
                          className="cursor-pointer"
                          onClick={onSaveNow}
                          disabled={putSettingsState.isLoading}
                        >
                          {t("Jetzt speichern")}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{t("Jetzt speichern")}</TooltipContent>
                    </Tooltip>
                  </div>
                </div>
              ) : null}
              <ServiceProgramEntryEditor
                entries={entries}
                groups={groups}
                groupsStickyTop={groupsStickyTop}
                disabled={putSettingsState.isLoading}
                onEntriesChange={(next) => {
                  setEntries(next);
                  setIsAutoSaveEnabled(true);
                }}
                onGroupsChange={(next) => {
                  setGroups(next);
                  setIsAutoSaveEnabled(true);
                }}
              />
            </>
          ) : (
            <div className="space-y-2">
              <div className="text-muted-foreground rounded-md border p-4 text-sm">
                {t(
                  "Das Leistungsprogramm ist noch nicht initialisiert. Klicken Sie auf Leistungsprogramm initialisieren.",
                )}
              </div>
              {!isInitPrerequisiteLoading && !canInitializeServiceProgram ? (
                <div className="text-muted-foreground rounded-md border p-4 text-sm">
                  {t(
                    "Für die Initialisierung müssen Schädlingskategorien, Schädlingsindikatoren und ein Monitor Katalog vorhanden sein.",
                  )}
                </div>
              ) : null}
            </div>
          )}
          {putSettingsState.error ? (
            <RTKQueryErrorAlert error={putSettingsState.error} />
          ) : null}
          {seedServiceProgramState.error ? (
            <RTKQueryErrorAlert error={seedServiceProgramState.error} />
          ) : null}
          {serviceProgramPreviewState.error ? (
            <RTKQueryErrorAlert error={serviceProgramPreviewState.error} />
          ) : null}
        </CardContent>
      </Card>
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="w-screen max-w-screen rounded-none sm:w-[96vw] sm:max-w-[96vw] sm:rounded-lg lg:w-[70vw] lg:max-w-[231mm]">
          <DialogHeader>
            <DialogTitle>{t("Leistungsprogramm Vorschau")}</DialogTitle>
          </DialogHeader>
          <DocumentSheetPreview
            objectURL={serviceProgramPreviewState.data?.objectURL}
            isLoading={
              serviceProgramPreviewState.isLoading ||
              serviceProgramPreviewState.isFetching
            }
            error={serviceProgramPreviewState.error}
            className="!md:col-span-1 w-full"
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
